"use client"

import { Deferred, Head, router } from "@inertiajs/react"
import {
  ArrowLeft,
  Eye,
  File02,
  MusicNote01,
  Send01,
  Star01,
  User01,
} from "@untitledui/icons"

import { UserActionsMenu } from "@/components/admin/users/actions-menu"
import { UserStatusBadge } from "@/components/admin/users/status-badge"
import { StatCard } from "@/components/dashboard/stat-card"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FileTrigger } from "@/components/ui/file-upload-trigger"
import { Link } from "@/components/ui/link"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { WarmStartStatusBadge } from "@/components/warm-starts/status-badge"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { formatDate } from "@/lib/utils/"
import {
  adminUsersPath,
  impersonateAdminUserPath,
  playlistPath,
  removeAvatarAdminUserPath,
  updateAvatarAdminUserPath,
} from "@/routes"
import type { UserDetail } from "@/types"

interface Stats {
  playlists: number
  published_playlists: number
  track_suggestions: number
  curated_briefs: number
  contributing_briefs: number
  warm_starts: number
  sessions: number
}

interface RecentPlaylist {
  id: number
  title: string
  published: boolean
  slug: string
  created_at: string
}

interface RecentSuggestion {
  id: number
  prompt_title: string
  brief_title: string
  status: string
  url: string | null
  created_at: string
}

interface RecentWarmStart {
  id: number
  status: string
  channel: string
  created_by_name: string
  created_at: string
}

interface SessionRow {
  id: number
  source: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

/**
 * What an expired impersonation interrupted, carried in the flash by
 * ApplicationController#end_expired_impersonation. path is where to pick up
 * again as this user: the page itself, or the form's page for a discarded post.
 */
interface Interrupted {
  user_id: number
  user_name: string
  method: string
  path: string | null
}

interface ShowProps {
  user: UserDetail
  stats: Stats
  /** Set when an expired impersonation of this user sent the admin here. */
  interrupted?: Interrupted | null
  recent_playlists?: RecentPlaylist[]
  recent_suggestions?: RecentSuggestion[]
  recent_warm_starts?: RecentWarmStart[]
  sessions?: SessionRow[]
}

export default function UserShow({
  user,
  stats,
  interrupted,
  recent_playlists,
  recent_suggestions,
  recent_warm_starts,
  sessions,
}: ShowProps) {
  const canImpersonate = user.status === "active" && !user.admin

  return (
    <DashboardLayout>
      <Head title={user.name} />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <Link
          href={adminUsersPath()}
          className="text-tertiary hover:text-secondary flex items-center gap-1.5 text-sm font-medium"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          All users
        </Link>

        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-4">
            <AvatarControls user={user} />
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
                  {user.name}
                </h1>
                <UserStatusBadge status={user.status} />
                {user.admin && (
                  <Badge color="brand" size="sm" type="pill-color">
                    Admin
                  </Badge>
                )}
                {user.verified && (
                  <Badge color="blue" size="sm" type="pill-color">
                    Verified
                  </Badge>
                )}
              </div>
              <p className="text-md text-tertiary">{user.email}</p>
              <p className="text-quaternary text-sm">
                Joined {formatDate(user.created_at)}
                {user.pronouns && <> · {user.pronouns}</>}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            {canImpersonate && (
              <Button
                size="md"
                color="secondary"
                iconLeading={Eye}
                onClick={() => router.post(impersonateAdminUserPath(user.id))}
              >
                Impersonate
              </Button>
            )}
            <UserActionsMenu user={user} />
          </div>
        </header>

        {interrupted && (
          <InterruptedBand
            interrupted={interrupted}
            userId={user.id}
            canImpersonate={canImpersonate}
          />
        )}

        {user.moderation_note && (
          <section
            aria-label="Moderation log"
            className="border-secondary bg-secondary rounded-xl border p-4"
          >
            <h2 className="text-primary text-sm font-semibold">
              Moderation log
            </h2>
            <pre className="text-tertiary font-body mt-1 text-sm whitespace-pre-wrap">
              {user.moderation_note}
            </pre>
          </section>
        )}

        <section
          aria-label="Contribution stats"
          className="grid grid-cols-2 gap-4 sm:grid-cols-3"
        >
          <StatCard
            icon={MusicNote01}
            label="Playlists"
            value={stats.playlists}
          />
          <StatCard
            icon={Star01}
            color="success"
            label="Published playlists"
            value={stats.published_playlists}
          />
          <StatCard
            icon={File02}
            label="Track suggestions"
            value={stats.track_suggestions}
          />
          <StatCard
            icon={File02}
            label="Curated briefs"
            value={stats.curated_briefs}
          />
          <StatCard
            icon={User01}
            label="Contributing briefs"
            value={stats.contributing_briefs}
          />
          <StatCard
            icon={Send01}
            label="Warm starts"
            value={stats.warm_starts}
          />
        </section>

        <Tabs orientation="horizontal" defaultSelectedKey="playlists">
          <TabList
            size="sm"
            type="underline"
            // A zero-count badge renders as a bare "0" glued to the label —
            // hide the badge entirely until there's something to count.
            items={[
              {
                id: "playlists",
                label: "Playlists",
                badge: stats.playlists || undefined,
              },
              {
                id: "suggestions",
                label: "Suggestions",
                badge: stats.track_suggestions || undefined,
              },
              {
                id: "warm-starts",
                label: "Warm starts",
                badge: stats.warm_starts || undefined,
              },
              {
                id: "sessions",
                label: "Sessions",
                badge: stats.sessions || undefined,
              },
            ]}
          />
          <Tabs.Panel id="playlists" className="pt-4">
            <Deferred data="recent_playlists" fallback={<TabSkeleton />}>
              <PlaylistsPanel rows={recent_playlists ?? []} />
            </Deferred>
          </Tabs.Panel>
          <Tabs.Panel id="suggestions" className="pt-4">
            <Deferred data="recent_suggestions" fallback={<TabSkeleton />}>
              <SuggestionsPanel rows={recent_suggestions ?? []} />
            </Deferred>
          </Tabs.Panel>
          <Tabs.Panel id="warm-starts" className="pt-4">
            <Deferred data="recent_warm_starts" fallback={<TabSkeleton />}>
              <WarmStartsPanel rows={recent_warm_starts ?? []} />
            </Deferred>
          </Tabs.Panel>
          <Tabs.Panel id="sessions" className="pt-4">
            <Deferred data="sessions" fallback={<TabSkeleton />}>
              <SessionsPanel rows={sessions ?? []} />
            </Deferred>
          </Tabs.Panel>
        </Tabs>
      </main>
    </DashboardLayout>
  )
}

// Headshot curation: real photos for staff / core supporters build trust on
// invite surfaces (warm-start claim, contributor lists); everyone else keeps
// the generated avatar, which is why there's no upload in user settings.
function AvatarControls({ user }: { user: UserDetail }) {
  const uploadAvatar = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    router.patch(updateAvatarAdminUserPath(user.id), { avatar: file })
  }

  return (
    <div className="flex flex-col items-center gap-1.5">
      <Avatar name={user.name} avatarUrl={user.avatar_url} size="xl" />
      <div className="flex items-center gap-2">
        <FileTrigger
          acceptedFileTypes={["image/png", "image/jpeg"]}
          onSelect={uploadAvatar}
        >
          <Button color="link-gray" size="sm">
            {user.avatar_url ? "Replace photo" : "Add photo"}
          </Button>
        </FileTrigger>
        {user.avatar_url && (
          <Button
            color="link-gray"
            size="sm"
            onClick={() => router.delete(removeAvatarAdminUserPath(user.id))}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  )
}

function TabSkeleton() {
  return (
    <div className="border-secondary bg-secondary h-24 animate-pulse rounded-xl border" />
  )
}

function PanelEmpty({ children }: { children: React.ReactNode }) {
  return <p className="text-tertiary py-6 text-center text-sm">{children}</p>
}

function PlaylistsPanel({ rows }: { rows: RecentPlaylist[] }) {
  if (rows.length === 0) return <PanelEmpty>No playlists yet.</PanelEmpty>
  return (
    <TableCard.Root>
      <Table aria-label="Recent playlists" size="sm" className="bg-primary">
        <Table.Header>
          <Table.Head id="title" isRowHeader label="Title" className="w-full" />
          <Table.Head id="state" label="State" />
          <Table.Head id="created" label="Created" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: RecentPlaylist) => (
            <Table.Row
              id={row.id}
              className="cursor-pointer"
              onAction={() => router.visit(playlistPath(row.slug))}
            >
              <Table.Cell className="text-primary text-sm font-medium">
                {row.title}
              </Table.Cell>
              <Table.Cell>
                <Badge
                  color={row.published ? "success" : "gray"}
                  size="sm"
                  type="pill-color"
                >
                  {row.published ? "Published" : "Draft"}
                </Badge>
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.created_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </TableCard.Root>
  )
}

function SuggestionsPanel({ rows }: { rows: RecentSuggestion[] }) {
  if (rows.length === 0) return <PanelEmpty>No suggestions yet.</PanelEmpty>
  return (
    <TableCard.Root>
      <Table aria-label="Recent suggestions" size="sm" className="bg-primary">
        <Table.Header>
          <Table.Head id="slot" isRowHeader label="Slot" className="w-full" />
          <Table.Head id="status" label="Status" />
          <Table.Head id="created" label="Suggested" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: RecentSuggestion) => (
            <Table.Row id={row.id}>
              <Table.Cell>
                <div>
                  <p className="text-primary text-sm font-medium">
                    {row.prompt_title}
                  </p>
                  <p className="text-tertiary text-sm">{row.brief_title}</p>
                </div>
              </Table.Cell>
              <Table.Cell>
                <Badge
                  color={
                    row.status === "accepted"
                      ? "success"
                      : row.status === "rejected"
                        ? "error"
                        : "gray"
                  }
                  size="sm"
                  type="pill-color"
                  className="capitalize"
                >
                  {row.status}
                </Badge>
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.created_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </TableCard.Root>
  )
}

function WarmStartsPanel({ rows }: { rows: RecentWarmStart[] }) {
  if (rows.length === 0) return <PanelEmpty>No warm starts.</PanelEmpty>
  return (
    <TableCard.Root>
      <Table aria-label="Warm starts" size="sm" className="bg-primary">
        <Table.Header>
          <Table.Head
            id="invited"
            isRowHeader
            label="Invited by"
            className="w-full"
          />
          <Table.Head id="channel" label="Channel" />
          <Table.Head id="status" label="Status" />
          <Table.Head id="created" label="Created" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: RecentWarmStart) => (
            <Table.Row id={row.id}>
              <Table.Cell className="text-primary text-sm font-medium">
                {row.created_by_name}
              </Table.Cell>
              <Table.Cell className="text-tertiary capitalize">
                {row.channel}
              </Table.Cell>
              <Table.Cell>
                <WarmStartStatusBadge status={row.status} />
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.created_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </TableCard.Root>
  )
}

function SessionsPanel({ rows }: { rows: SessionRow[] }) {
  if (rows.length === 0) return <PanelEmpty>No active sessions.</PanelEmpty>
  return (
    <TableCard.Root>
      <Table aria-label="Active sessions" size="sm" className="bg-primary">
        <Table.Header>
          <Table.Head
            id="device"
            isRowHeader
            label="Device"
            className="w-full"
          />
          <Table.Head id="source" label="Source" />
          <Table.Head id="ip" label="IP" />
          <Table.Head id="created" label="Started" />
        </Table.Header>
        <Table.Body items={rows}>
          {(row: SessionRow) => (
            <Table.Row id={row.id}>
              <Table.Cell className="text-tertiary max-w-xs truncate text-sm">
                {row.user_agent ?? "Unknown"}
              </Table.Cell>
              <Table.Cell className="text-tertiary capitalize">
                {row.source ?? "sign-in"}
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap tabular-nums">
                {row.ip_address ?? "—"}
              </Table.Cell>
              <Table.Cell className="text-tertiary whitespace-nowrap">
                {formatDate(row.created_at)}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </TableCard.Root>
  )
}

/*
 * The hour impersonating this person ran out mid-request. Says what was
 * interrupted and, for a page, offers to pick it up again as them; a form
 * post was discarded on purpose and has to be redone once back as them.
 * Warning tokens, as the banner: a caution state, not an action.
 */
function InterruptedBand({
  interrupted,
  userId,
  canImpersonate,
}: {
  interrupted: Interrupted
  userId: number
  canImpersonate: boolean
}) {
  const first = interrupted.user_name.split(" ")[0]
  const wasPage = interrupted.method === "GET" || interrupted.method === "HEAD"
  const resume = () =>
    router.post(impersonateAdminUserPath(userId), {
      return_to: interrupted.path,
    })

  return (
    <section
      role="status"
      aria-label="Impersonation ended"
      className="bg-warning-primary border-warning-300 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border p-4"
    >
      <p className="text-warning-primary grow text-sm">
        <span className="font-semibold">
          Your hour impersonating {interrupted.user_name} ran out.
        </span>{" "}
        You are yourself again.{" "}
        {wasPage ? (
          <>
            You were on <code className="font-mono">{interrupted.path}</code>.
          </>
        ) : (
          <>
            A {interrupted.method} to{" "}
            <code className="font-mono">{interrupted.path ?? "a page"}</code>{" "}
            was not sent, so nothing was changed in your name: impersonate{" "}
            {first} again and resubmit it.
          </>
        )}
      </p>
      {canImpersonate && interrupted.path && (
        <Button size="sm" color="secondary" iconLeading={Eye} onClick={resume}>
          {wasPage ? `Pick up as ${first}` : `Impersonate ${first} again`}
        </Button>
      )}
    </section>
  )
}
